import { type ReactNode } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { DataLoader } from '@/components/ui/DataLoader';
import {
  useFundFigures,
  useFundMetricsSeries,
} from '@/hooks/react-query/useFunds';
import type {
  Fund,
  FundFigure,
  FundFigureKey,
  FundMetricsSeries,
} from '@/types/fund';
import {
  useFundInvestorStatistics,
  useFundInvestors,
} from '@/hooks/react-query/useInvestors';
import { FigureChart } from './figures/FigureChart';
import { FigureTile } from './figures/FigureTile';
import { FundSummaryCards } from './FundSummaryCards';

const TILE_FIGURES: FundFigureKey[] = [
  'gross_moic',
  'tvpi',
  'dpi',
  'rvpi',
  'net_irr',
  'nav',
  'total_called',
  'invested_capital',
  'uncalled_capital',
  'cash_balance',
];

const CHART_FIGURES: FundFigureKey[] = ['nav', 'tvpi'];

function pick(figures: FundFigure[], keys: FundFigureKey[]) {
  return keys.flatMap((key) => figures.filter((f) => f.figure === key));
}

interface FundOverviewV2TabProps {
  fund: Fund;
}

// Reachable only via ?tab=overview-v2 until it replaces the Overview tab (DFF-876).
export function FundOverviewV2Tab({ fund }: FundOverviewV2TabProps) {
  return (
    <TabsContent
      value="overview-v2"
      className="flex-1 space-y-6"
      data-testid="fund-overview-v2"
    >
      <FundSummary fund={fund} />
      <FundOverviewV2 fund={fund} />
    </TabsContent>
  );
}

function FundSummary({ fund }: FundOverviewV2TabProps) {
  const { data: fundStats } = useFundInvestorStatistics(fund.id);
  const { data: fundInvestors } = useFundInvestors(fund.id);

  return (
    <FundSummaryCards
      fund={fund}
      investorCount={fundStats?.total_investors ?? fundInvestors?.length ?? 0}
      totalCommitments={fundStats?.total_commitments ?? 0}
    />
  );
}

function FundOverviewV2({ fund }: FundOverviewV2TabProps) {
  if (!fund.first_closing_date) {
    return (
      <FiguresNotice>Figures appear after the first closing.</FiguresNotice>
    );
  }
  return <FundFigures fund={fund} />;
}

function FiguresNotice({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function FundFigures({ fund }: FundOverviewV2TabProps) {
  const seriesQuery = useFundMetricsSeries(fund.id);
  const figuresQuery = useFundFigures();

  return (
    <DataLoader query={seriesQuery}>
      {(series: FundMetricsSeries) =>
        series.points.length === 0 ? (
          <FiguresNotice>
            Figures appear after the first quarter end.
          </FiguresNotice>
        ) : (
          <DataLoader query={figuresQuery}>
            {(figures: FundFigure[]) => (
              <div className="space-y-6">
                <section className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border shadow-sm xl:grid-cols-5">
                  {pick(figures, TILE_FIGURES).map((figure) => (
                    <FigureTile
                      key={figure.figure}
                      figure={figure}
                      points={series.points}
                      currency={series.currency}
                    />
                  ))}
                </section>
                <section className="space-y-4">
                  <div className="flex flex-wrap items-baseline gap-3">
                    <h2 className="text-lg font-semibold tracking-tight">
                      Performance
                    </h2>
                    <span className="text-[13px] text-muted-foreground">
                      Net of fees and carried interest · quarter-ends up to the
                      as-of date
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {CHART_FIGURES.map((key) => (
                      <FigureChart
                        key={key}
                        figures={figures}
                        initialFigure={key}
                        points={series.points}
                        currency={series.currency}
                      />
                    ))}
                  </div>
                </section>
              </div>
            )}
          </DataLoader>
        )
      }
    </DataLoader>
  );
}
